"use client";

/**
 * fund-portfolio-metrics.tsx
 *
 * Two inline displays for the Portfolio Holdings section:
 *   1. Concentration line: "Top 5: X% | Top 10: Y%"
 *   2. Asset allocation stacked bar (Equity / Debt / Cash / Other)
 *
 * Either piece hides itself if its data is null. If both are null the whole
 * card is hidden.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Portfolio Holdings section)
 */

export interface FundPortfolioMetricsProps {
  top5ConcentrationPct: number | null;
  top10ConcentrationPct: number | null;
  equityPct: number | null;
  debtPct: number | null;
  otherPct: number | null;
  cashPct?: number | null;
}

interface Segment {
  label: string;
  value: number;
  color: string;
}

export function FundPortfolioMetrics({
  top5ConcentrationPct,
  top10ConcentrationPct,
  equityPct,
  debtPct,
  otherPct,
  cashPct,
}: FundPortfolioMetricsProps) {
  const hasConcentration =
    (top5ConcentrationPct !== null && top5ConcentrationPct !== undefined) ||
    (top10ConcentrationPct !== null && top10ConcentrationPct !== undefined);

  const segments: Segment[] = [];
  if (equityPct !== null && equityPct !== undefined && equityPct > 0) {
    segments.push({ label: "Equity", value: equityPct, color: "#3B82F6" });
  }
  if (debtPct !== null && debtPct !== undefined && debtPct > 0) {
    segments.push({ label: "Debt", value: debtPct, color: "#8B5CF6" });
  }
  if (cashPct !== null && cashPct !== undefined && cashPct > 0) {
    segments.push({ label: "Cash", value: cashPct, color: "#10B981" });
  }
  if (otherPct !== null && otherPct !== undefined && otherPct > 0) {
    segments.push({ label: "Other", value: otherPct, color: "#64748B" });
  }

  const hasAllocation = segments.length > 0;

  if (!hasConcentration && !hasAllocation) return null;

  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5 space-y-4">
      {hasConcentration && (
        <div>
          <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-2">
            Concentration
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            {top5ConcentrationPct !== null &&
              top5ConcentrationPct !== undefined && (
                <div>
                  <span className="text-dim text-[11px] mr-2">Top 5</span>
                  <span className="text-foreground text-sm font-bold font-tabular">
                    {top5ConcentrationPct.toFixed(1)}%
                  </span>
                </div>
              )}
            {top10ConcentrationPct !== null &&
              top10ConcentrationPct !== undefined && (
                <div>
                  <span className="text-dim text-[11px] mr-2">Top 10</span>
                  <span className="text-foreground text-sm font-bold font-tabular">
                    {top10ConcentrationPct.toFixed(1)}%
                  </span>
                </div>
              )}
          </div>
        </div>
      )}

      {hasAllocation && (
        <div>
          <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-2">
            Asset allocation
          </div>
          {/* Stacked bar */}
          <div className="h-3 rounded-full overflow-hidden flex bg-surface-light/10">
            {segments.map((seg) => (
              <div
                key={seg.label}
                className="h-full transition-all duration-700"
                style={{
                  width: `${(seg.value / total) * 100}%`,
                  backgroundColor: seg.color,
                }}
                title={`${seg.label}: ${seg.value.toFixed(1)}%`}
              />
            ))}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {segments.map((seg) => (
              <div
                key={seg.label}
                className="flex items-center gap-1.5 text-[11px]"
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-muted-foreground">{seg.label}</span>
                <span className="text-foreground font-tabular font-semibold">
                  {seg.value.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
